import type { PresetPermission } from "@pax/api-contract/presets/v1alpha1";
import { z } from "zod";

export type Json = z.infer<ReturnType<typeof z.json>>;
export type Fields = Record<string, Json>;

/**
 * What a draft holds, as records under the World's own ids where the World has them: a thing on a
 * map, an entity holding ground, a skill or one of its references, a perspective, a recommendation
 * the lobby offers, the world's own settings, such as when it starts, what the page will say once
 * the draft is published.
 */
export const COLLECTIONS = [
  "thing",
  "entity",
  "skill",
  "reference",
  "perspective",
  "recommendation",
  "world",
  "page",
] as const;
export type Collection = (typeof COLLECTIONS)[number];

/**
 * A record's key, `collection:id`. A thing's id names the map it's on first:
 * `thing:spectator/region:257`, or a perspective's id in place of `spectator`. A reference's
 * names its skill: `reference:schlieffen/wheel`. The world's settings are `world:main`, and the
 * page is `page:main`.
 */
export const RecordKey = z
  .string()
  .max(240)
  .regex(new RegExp(`^(${COLLECTIONS.join("|")}):\\S+$`))
  .meta({ id: "DraftRecordKey", description: "A record's collection and id: `skill:rivers`." });
export type RecordKey = z.infer<typeof RecordKey>;

/** The draft's page, as a record: its name and change log, description, and listing. */
export const PAGE_KEY = "page:main";

/** The world's own settings, as a record: when it starts. */
export const WORLD_KEY = "world:main";

const Value = z.record(z.string(), z.json());

/**
 * Where a text is in a record: one of its fields. Only a plain string there is text people type
 * into together.
 */
export const TextPath = z.enum(["name", "description", "premise", "body", "text", "changeLog"]);
export type TextPath = z.infer<typeof TextPath>;

/**
 * One change to one record. `create` refuses a key that exists, so two editors choosing the same
 * id at once can't overwrite each other. `update` writes only the fields it names, and a field set
 * to null is unset. `reset` forgets every change the draft made to the record, back to what it
 * began with. `text` merges someone's typing into a text: a Yjs update, made against the text as
 * it began at revision `since`.
 */
export const DraftOp = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("create"), key: RecordKey, value: Value }),
  z.strictObject({ type: z.literal("set"), key: RecordKey, value: Value }),
  z.strictObject({ type: z.literal("update"), key: RecordKey, fields: Value }),
  z.strictObject({ type: z.literal("remove"), key: RecordKey }),
  z.strictObject({ type: z.literal("reset"), key: RecordKey }),
  z.strictObject({
    type: z.literal("text"),
    key: RecordKey,
    path: TextPath,
    since: z.number().int().nonnegative(),
    update: z.base64().max(800_000),
  }),
]);
export type DraftOp = z.infer<typeof DraftOp>;
export type StructuralOp = Exclude<DraftOp, { type: "text" }>;
export type TextOp = Extract<DraftOp, { type: "text" }>;

/** What a draft holds for one record: everything written to it since the draft began. */
export interface Entry {
  /** The fields written, or the whole record when `whole`. Null once removed. */
  value: Fields | null;
  /** Whether `value` is the whole record, or changes over what the draft began with. */
  whole: boolean;
  /** For each field, and `$` for the record itself, the revision that last wrote it. */
  revisions: Record<string, number>;
  /**
   * For each field, and `$` for the record itself, the revision that last wrote it whole, rather
   * than by typing into it. A text begins again from that revision.
   */
  replaced: Record<string, number>;
}

/** Fields whose changes can't be undone in part: a shape is either moved back or it isn't. */
export const GEOMETRY_FIELDS: ReadonlySet<string> = new Set([
  "$",
  "geometry",
  "position",
  "path",
  "box",
  "points",
  "line",
  "area",
]);

export const collectionOf = (key: string) => key.slice(0, key.indexOf(":")) as Collection;
export const idOf = (key: string) => key.slice(key.indexOf(":") + 1);
export const topFieldOf = (path: string) => path.split(".")[0] as string;

/** The permission that changing a record takes, by its roles alone. */
export function permissionFor(key: string): PresetPermission {
  switch (collectionOf(key)) {
    case "thing":
    case "entity":
      return "editMap";
    case "skill":
    case "reference":
      return "editSkills";
    case "perspective":
    case "recommendation":
      return "editPerspectives";
    case "world":
    case "page":
      return "editOverview";
  }
}

/**
 * What a draft began with, as reading one record of it goes: a map of them, or a store that reads
 * the one asked for.
 */
export interface BaseRecords {
  get(key: string): Fields | undefined;
}

/** A record as the draft has it now: what it began with, under every change made to it since. */
export function recordOf(
  key: string,
  entries: ReadonlyMap<string, Entry>,
  base: BaseRecords,
): Fields | null {
  const entry = entries.get(key);
  const began = base.get(key) ?? null;
  if (!entry) return began;
  if (entry.value === null) return null;
  if (entry.whole) return entry.value;
  if (!began) return null;
  const merged: Fields = { ...began };
  for (const [field, value] of Object.entries(entry.value)) {
    if (value === null) delete merged[field];
    else merged[field] = value;
  }
  return merged;
}

/** The string at a text path, or undefined when there isn't a plain string there to type into. */
export function textAt(record: Fields | null, path: TextPath): string | undefined {
  const value = record?.[path];
  return typeof value === "string" ? value : undefined;
}

/** The revision a record's text at `path` began from: the last time it was written whole. */
export function generationOf(entry: Entry | undefined, path: TextPath): number {
  if (!entry) return 0;
  return Math.max(entry.replaced.$ ?? 0, entry.replaced[path] ?? 0);
}

/**
 * One structural op applied to a draft's entries at a revision. The draft session applies it, and
 * every editor applies the same op the same way, so they all end up holding the same thing.
 */
export function applyOp(entries: Map<string, Entry>, op: StructuralOp, revision: number): void {
  const entry = entries.get(op.key);
  switch (op.type) {
    case "create":
    case "set": {
      const revisions: Record<string, number> = { $: revision };
      for (const field of Object.keys(op.value)) revisions[field] = revision;
      entries.set(op.key, { value: op.value, whole: true, revisions, replaced: { $: revision } });
      return;
    }
    case "update": {
      if (entry?.value === null) return;
      const whole = entry?.whole ?? false;
      const value: Fields = { ...(entry?.value ?? {}) };
      const revisions = { ...(entry?.revisions ?? {}) };
      const replaced = { ...(entry?.replaced ?? {}) };
      for (const [field, next] of Object.entries(op.fields)) {
        if (next === null && whole) delete value[field];
        else value[field] = next;
        revisions[field] = revision;
        replaced[field] = revision;
      }
      entries.set(op.key, { value, whole, revisions, replaced });
      return;
    }
    case "remove":
      entries.set(op.key, {
        value: null,
        whole: true,
        revisions: { $: revision },
        replaced: { $: revision },
      });
      return;
    case "reset":
      entries.set(op.key, {
        value: {},
        whole: false,
        revisions: { $: revision },
        replaced: { $: revision },
      });
  }
}

/** A record's text at `path` set to what typing into it made, at a revision. */
export function writeText(
  entries: Map<string, Entry>,
  base: BaseRecords,
  key: string,
  path: TextPath,
  text: string,
  revision: number,
): void {
  const entry = entries.get(key);
  entries.set(key, {
    value: { ...(entry?.value ?? {}), [path]: text },
    whole: entry?.whole ?? false,
    revisions: { ...(entry?.revisions ?? {}), [path]: revision },
    replaced: entry?.replaced ?? {},
  });
}
